import { Component } from '@theme/component';
import { mediaQueryLarge, prefersReducedMotion, requestIdleCallback } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents } from '@theme/events';

/** @type {Record<string, { mobile: number; desktop: number }>} */
const DENSITY_MAP = {
  'layout-1': { mobile: 1, desktop: 3 },
  'layout-2': { mobile: 2, desktop: 4 },
  'layout-3': { mobile: 3, desktop: 6 },
};

const DEFAULT_LAYOUT = 'layout-2';

/**
 * Applies client-side collection grid density from the utilities layout picker,
 * and handles load-more / infinite-scroll pagination via the Section Rendering API.
 *
 * @typedef {Object} CollectionGridLayoutRefs
 * @property {HTMLElement} grid - The product grid list element.
 * @property {HTMLButtonElement} [loadMore] - The "Load more" button.
 * @property {HTMLElement} [loadMoreSentinel] - The invisible intersection sentinel.
 * @property {HTMLElement} [progressCount] - Seen-count text node container.
 * @property {HTMLElement} [progressFill] - Progress bar fill element.
 * @property {HTMLElement} [progress] - Progress wrapper (progressbar role).
 *
 * @extends {Component<CollectionGridLayoutRefs>}
 */
class CollectionGridLayoutComponent extends Component {
  /** @type {Map<number, string>} */
  #pages = new Map();

  /** @type {Map<number, Promise<void>>} */
  #pendingPages = new Map();

  /**
   * Cards inserted above the initial page. They are already covered by the
   * server "seen" offset, so the progress count must not add them again.
   * @type {number}
   */
  #prependedCount = 0;

  /** @type {number} */
  #fetchGeneration = 0;

  /** @type {IntersectionObserver | undefined} */
  #autoLoadObserver;

  /** @type {boolean} */
  #isLoading = false;

  /** @type {boolean} */
  #catchupScheduled = false;

  /** @type {number} */
  #pageLabelFrame = 0;

  /**
   * Page last written into the toolbar label. `0` means unset.
   * @type {number}
   */
  #visiblePage = 0;

  /**
   * Number of cards rendered server-side on initial load.
   * Used to calculate the offset when landing on page > 1.
   * @type {number}
   */
  #initialDomCount = 0;

  connectedCallback() {
    super.connectedCallback();
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.#applyStoredLayout();

    const { grid } = this.refs;
    if (grid) {
      this.#initialDomCount = grid.querySelectorAll(':scope > [ref="cards[]"]').length;
    }

    this.#observeAutoLoad();
    this.#prefetchNextPage();
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
    window.addEventListener('scroll', this.#onScroll, { passive: true });
    this.#updatePageNav();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
    window.removeEventListener('scroll', this.#onScroll);
    cancelAnimationFrame(this.#pageLabelFrame);
    this.#pageLabelFrame = 0;
    this.#autoLoadObserver?.disconnect();
  }

  // ─── Layout density ──────────────────────────────────────────────────────────

  /**
   * @param {Event} event
   */
  updateLayout(event) {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;

    this.#setLayout(target.value);

    const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
    sessionStorage.setItem(`collection-grid-layout-${viewport}`, target.value);
  }

  #handleMediaQueryChange = () => {
    this.#applyStoredLayout();
  };

  #applyStoredLayout() {
    const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
    const stored = sessionStorage.getItem(`collection-grid-layout-${viewport}`) || DEFAULT_LAYOUT;
    const input = this.querySelector(`input[value="${stored}"]`);

    if (input instanceof HTMLInputElement) {
      input.checked = true;
    }

    this.#setLayout(stored);
  }

  /**
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!(grid instanceof HTMLElement)) return;

    const config = DENSITY_MAP[value] ?? DENSITY_MAP[DEFAULT_LAYOUT];
    const columns = mediaQueryLarge.matches ? config.desktop : config.mobile;

    grid.style.setProperty('--collection-grid-columns', String(columns));
  }

  // ─── Pagination ───────────────────────────────────────────────────────────────

  /**
   * Public handler wired from the "Load more" button via `on:click="/loadMore"`.
   */
  loadMore() {
    this.#renderNextPage();
  }

  /**
   * Public handler wired from the toolbar page control via `on:click="#product-grid/loadPrevious"`.
   * Inserts the page before the first rendered page at the top of the grid.
   */
  loadPrevious() {
    this.#renderPreviousPage();
  }

  /**
   * Public handler wired from the double-back control via `on:click="#product-grid/goToFirstPage"`.
   * Scrolls to the first page-1 card when it is already in the grid; otherwise the link navigates to page 1.
   * @param {Event} event
   */
  goToFirstPage(event) {
    if (this.#isModifiedClick(event)) return;

    const firstPageCard = this.#firstPageCard();
    if (!firstPageCard) return;

    event.preventDefault();
    this.#syncUrlToFirstPage();
    this.#revealPrependedItem(firstPageCard);
  }

  /**
   * Appends the next page of product cards to the grid.
   */
  async #renderNextPage() {
    if (this.#isLoading) return;

    const { grid, loadMore } = this.refs;
    if (!grid) return;

    const nextPage = this.#getNextPage();
    if (!nextPage || !this.#isValidPage(nextPage.page, grid)) return;

    this.#isLoading = true;
    if (loadMore instanceof HTMLButtonElement) {
      loadMore.disabled = true;
      loadMore.setAttribute('aria-busy', 'true');
    }

    try {
      await this.#ensurePage(nextPage.page, nextPage.url);
      const items = this.#getGridItems(nextPage.page);
      if (!items || items.length === 0) return;

      grid.append(...items);
      history.pushState('', '', nextPage.url.toString());
      this.#updateProgress();
      this.#updatePageNav();

      requestIdleCallback(() => {
        this.#prefetchNextPage();
      });

      // Catch up if the sentinel is still near the viewport after appending.
      this.#scheduleCatchup();
    } finally {
      this.#isLoading = false;
      if (loadMore instanceof HTMLButtonElement) {
        loadMore.disabled = false;
        loadMore.removeAttribute('aria-busy');
      }
    }
  }

  /**
   * Prepends the page before the first rendered card.
   */
  async #renderPreviousPage() {
    if (this.#isLoading) return;

    const { grid } = this.refs;
    if (!grid) return;

    const previousPage = this.#getPreviousPage();
    if (!previousPage || !this.#isValidPage(previousPage.page, grid)) return;
    if (grid.querySelector(`:scope > [data-page="${previousPage.page}"]`)) return;

    const previousButton = this.#pagePreviousButton();
    this.#isLoading = true;
    if (previousButton instanceof HTMLButtonElement) {
      previousButton.disabled = true;
      previousButton.setAttribute('aria-busy', 'true');
    }

    try {
      await this.#ensurePage(previousPage.page, previousPage.url);
      const items = this.#getGridItems(previousPage.page);
      if (!items || items.length === 0) return;

      const firstCard = grid.querySelector(':scope > [ref="cards[]"]');
      const firstItem = items[0];
      if (firstCard) {
        firstCard.before(...items);
      } else {
        grid.prepend(...items);
      }

      this.#prependedCount += items.length;
      this.#updateProgress();
      this.#updatePageNav();
      this.#revealPrependedItem(firstItem);
    } finally {
      this.#isLoading = false;
      previousButton?.removeAttribute('aria-busy');
      this.#updatePageNav();
    }
  }

  /**
   * Fetches a page once and shares the in-flight request across append, prepend, and prefetch.
   * @param {number} pageNumber
   * @param {URL} [url]
   */
  async #ensurePage(pageNumber, url) {
    if (this.#pages.has(pageNumber)) return;

    const pending = this.#pendingPages.get(pageNumber);
    if (pending) {
      await pending;
      return;
    }

    const request = this.#fetchPage(pageNumber, url);
    this.#pendingPages.set(pageNumber, request);

    try {
      await request;
    } finally {
      this.#pendingPages.delete(pageNumber);
    }
  }

  /**
   * Fetches and caches a specific page using the Section Rendering API.
   * @param {number} pageNumber
   * @param {URL} [url]
   */
  async #fetchPage(pageNumber, url) {
    const generation = this.#fetchGeneration;

    if (!url) {
      url = new URL(window.location.href);
      url.searchParams.set('page', String(pageNumber));
      url.hash = '';
    }

    // Pass a clone so buildSectionRenderingURL's mutation (adding section_id)
    // doesn't contaminate the original url used later for history.pushState.
    const html = await sectionRenderer.getSectionHTML(this.sectionId, true, new URL(url));
    if (generation !== this.#fetchGeneration) return;

    this.#pages.set(pageNumber, html);
  }

  /**
   * Idle-prefetches the next page into the cache.
   */
  #prefetchNextPage() {
    const { grid } = this.refs;
    if (!grid) return;

    const nextPage = this.#getNextPage();
    if (!nextPage || !this.#isValidPage(nextPage.page, grid)) return;
    if (this.#pages.has(nextPage.page)) return;

    requestIdleCallback(() => {
      this.#ensurePage(nextPage.page, nextPage.url);
    });
  }

  /**
   * Parses the cached HTML for a page and extracts its grid item elements.
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridItems(page) {
    const html = this.#pages.get(page);
    if (!html) return;

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const gridEl = doc.querySelector('[ref="grid"]');
    if (!gridEl) return;

    return gridEl.querySelectorAll(':scope > [ref="cards[]"]');
  }

  /**
   * Returns the next page number and URL derived from the last rendered card.
   * @returns {{ page: number; url: URL } | undefined}
   */
  #getNextPage() {
    const { grid } = this.refs;
    if (!grid) return;

    const cards = Array.from(grid.querySelectorAll(':scope > [ref="cards[]"]'));
    const lastCard = cards[cards.length - 1];
    if (!(lastCard instanceof HTMLElement)) return;

    const currentPage = Number(lastCard.dataset.page) || 1;
    const nextPageNumber = currentPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', String(nextPageNumber));
    url.hash = '';

    return { page: nextPageNumber, url };
  }

  /**
   * Returns the page before the first rendered card.
   * @returns {{ page: number; url: URL } | undefined}
   */
  #getPreviousPage() {
    const { grid } = this.refs;
    if (!grid) return;

    const { first } = this.#getRenderedPageRange();
    const previousPageNumber = first - 1;
    if (previousPageNumber < 1) return;

    const url = new URL(window.location.href);
    url.searchParams.set('page', String(previousPageNumber));
    url.hash = '';

    return { page: previousPageNumber, url };
  }

  /**
   * Lowest and highest `data-page` currently rendered in the grid.
   * @returns {{ first: number; last: number }}
   */
  #getRenderedPageRange() {
    const { grid } = this.refs;
    if (!grid) return { first: 1, last: 1 };

    let first = Infinity;
    let last = 0;

    for (const card of grid.querySelectorAll(':scope > [ref="cards[]"]')) {
      if (!(card instanceof HTMLElement)) continue;

      const page = Number(card.dataset.page) || 1;
      if (page < first) first = page;
      if (page > last) last = page;
    }

    if (last === 0) return { first: 1, last: 1 };

    return { first, last };
  }

  /**
   * @param {number} page
   * @param {HTMLElement} grid
   */
  #isValidPage(page, grid) {
    const lastPage = Number(grid.dataset.lastPage) || 1;
    return page >= 1 && page <= lastPage;
  }

  /**
   * Toolbar page controls. Previous is disabled once page 1 is in the grid.
   * The double-arrow link appears once a later page is rendered.
   * The visible number follows the card under the sticky toolbar.
   */
  #updatePageNav() {
    const nav = this.#pageNav();
    if (!nav) return;

    const { first, last } = this.#getRenderedPageRange();
    nav.hidden = false;

    const restart = this.#pageRestartLink();
    if (restart) {
      restart.hidden = last <= 1;
      const scrollLabel = restart.dataset.labelScroll;
      const navigateLabel = restart.dataset.labelNavigate;
      const label = first <= 1 ? scrollLabel : navigateLabel;
      if (label) restart.setAttribute('aria-label', label);
    }

    const previous = this.#pagePreviousButton();
    if (!previous) return;

    previous.disabled = first <= 1;

    const icon = previous.querySelector('.collection-utilities__page-previous-icon');
    if (icon instanceof HTMLElement) icon.hidden = first <= 1;

    this.#updatePageLabel();
  }

  /**
   * Writes the toolbar page number from the product under the sticky bar.
   */
  #updatePageLabel() {
    const previous = this.#pagePreviousButton();
    if (!previous) return;

    const page = this.#pageAtReadingLine();
    if (page === this.#visiblePage) return;

    this.#visiblePage = page;

    const label = previous.querySelector('.collection-utilities__page-label');
    const template = previous.dataset.pageLabel;
    if (label && template) {
      label.textContent = template.replace('[current]', String(page));
    }
  }

  /**
   * `data-page` of the last product card whose top is at or above the toolbar.
   * @returns {number}
   */
  #pageAtReadingLine() {
    const { grid } = this.refs;
    if (!grid) return 1;

    const cards = grid.querySelectorAll(':scope > [data-page]');
    if (cards.length === 0) return 1;

    const bar = this.#utilitiesBar();
    const line = bar instanceof HTMLElement ? bar.getBoundingClientRect().bottom : 0;

    let low = 0;
    let high = cards.length - 1;
    let match = cards[0];

    while (low <= high) {
      const mid = (low + high) >> 1;
      const card = cards[mid];
      if (!(card instanceof HTMLElement)) {
        high = mid - 1;
        continue;
      }

      if (card.getBoundingClientRect().top <= line) {
        match = card;
        low = mid + 1;
      } else {
        high = mid - 1;
      }
    }

    return Number(match instanceof HTMLElement ? match.dataset.page : 1) || 1;
  }

  #schedulePageLabel() {
    if (this.#pageLabelFrame) return;

    this.#pageLabelFrame = requestAnimationFrame(() => {
      this.#pageLabelFrame = 0;
      this.#updatePageLabel();
    });
  }

  /**
   * @returns {Element | null}
   */
  #hostSection() {
    return this.closest('.shopify-section');
  }

  /**
   * @returns {HTMLElement | null}
   */
  #pageNav() {
    const nav = this.#hostSection()?.querySelector('.collection-utilities__page-nav');
    return nav instanceof HTMLElement ? nav : null;
  }

  /**
   * @returns {HTMLAnchorElement | null}
   */
  #pageRestartLink() {
    const link = this.#pageNav()?.querySelector('.collection-utilities__page-restart');
    return link instanceof HTMLAnchorElement ? link : null;
  }

  /**
   * @returns {HTMLButtonElement | null}
   */
  #pagePreviousButton() {
    const button = this.#pageNav()?.querySelector('.collection-utilities__page-previous');
    return button instanceof HTMLButtonElement ? button : null;
  }

  /**
   * @returns {HTMLElement | null}
   */
  #utilitiesBar() {
    const bar = this.#hostSection()?.querySelector(
      '.collection-utilities-bar, .search__utilities-bar'
    );
    return bar instanceof HTMLElement ? bar : null;
  }

  /**
   * @returns {HTMLElement | null}
   */
  #firstPageCard() {
    const { grid } = this.refs;
    const card = grid?.querySelector(':scope > [data-page="1"]');
    return card instanceof HTMLElement ? card : null;
  }

  /**
   * Cmd/Ctrl/Shift/Alt (or non-primary button) must keep the native link navigation.
   * @param {Event} event
   * @returns {boolean}
   */
  #isModifiedClick(event) {
    return (
      event instanceof MouseEvent &&
      (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0)
    );
  }

  /**
   * Drops `page` from the address bar so a refresh matches the in-memory page-1 cards.
   */
  #syncUrlToFirstPage() {
    const url = new URL(window.location.href);
    if (!url.searchParams.has('page')) return;

    url.searchParams.delete('page');
    url.hash = '';
    history.replaceState('', '', url.toString());
  }

  /**
   * Brings a card up to just under the sticky toolbar.
   * @param {Element | undefined} item
   */
  #revealPrependedItem(item) {
    if (!(item instanceof HTMLElement)) return;

    const bar = this.#utilitiesBar();
    const offset = bar instanceof HTMLElement ? bar.getBoundingClientRect().bottom : 0;
    const top = item.getBoundingClientRect().top - offset;
    if (Math.abs(top) < 1) return;

    window.scrollBy({
      top,
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    });
  }

  /**
   * Updates the progress bar, seen-count text, and button/sentinel visibility.
   */
  #updateProgress() {
    const { grid, loadMore, loadMoreSentinel, progressCount, progressFill, progress } = this.refs;
    if (!grid) return;

    const total = Number(grid.dataset.totalProducts) || 0;
    // `data-initial-seen` reflects the server-rendered offset (e.g. 48 when landing
    // on page 3). We add only the items JS has appended since page load.
    const initialSeen = Number(grid.dataset.initialSeen) || 0;
    const currentDomCount = grid.querySelectorAll(':scope > [ref="cards[]"]').length;
    const addedByJs = Math.max(currentDomCount - this.#initialDomCount - this.#prependedCount, 0);
    const seen = Math.min(initialSeen + addedByJs, total);
    const lastPage = Number(grid.dataset.lastPage) || 1;
    const nextPage = this.#getNextPage();
    const isLastPage = !nextPage || !this.#isValidPage(nextPage.page, grid);

    if (progressCount instanceof HTMLElement) {
      progressCount.textContent = String(seen);
    }

    if (progressFill instanceof HTMLElement) {
      const pct = total > 0 ? Math.round((seen / total) * 100) : 100;
      progressFill.style.setProperty('--progress', `${pct}%`);
    }

    if (progress instanceof HTMLElement) {
      progress.setAttribute('aria-valuenow', String(seen));
      progress.setAttribute('aria-valuemax', String(total));
    }

    if (isLastPage) {
      loadMore?.remove();
      loadMoreSentinel?.remove();
      this.#autoLoadObserver?.disconnect();
    }
  }

  /**
   * Schedules a rAF-coalesced catch-up load. Only fires if the sentinel is
   * still visible in or just below the viewport (0 < top <= innerHeight + margin).
   * When top <= 0 the user has already scrolled past the grid to the footer —
   * auto-load is intentionally suppressed so the footer stays reachable.
   */
  #scheduleCatchup() {
    if (this.dataset.autoLoad !== 'true' || this.#catchupScheduled) return;
    this.#catchupScheduled = true;
    requestAnimationFrame(() => {
      this.#catchupScheduled = false;
      const { loadMoreSentinel } = this.refs;
      if (!loadMoreSentinel) return;
      const top = loadMoreSentinel.getBoundingClientRect().top;
      if (top > 0 && top <= window.innerHeight + 200) {
        this.#renderNextPage();
      }
    });
  }

  /** @type {() => void} */
  #onScroll = () => {
    this.#scheduleCatchup();
    this.#schedulePageLabel();
  };

  /**
   * Sets up an IntersectionObserver on the sentinel when auto-load is enabled.
   */
  #observeAutoLoad() {
    if (this.dataset.autoLoad !== 'true') return;

    const { loadMoreSentinel } = this.refs;
    if (!loadMoreSentinel) return;

    this.#autoLoadObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.#renderNextPage();
          }
        }
      },
      { rootMargin: '100px' }
    );

    this.#autoLoadObserver.observe(loadMoreSentinel);
  }

  /**
   * Clears the page cache on filter/sort updates so stale pages aren't appended
   * after the section has been morphed back to page 1.
   */
  #handleFilterUpdate = () => {
    this.#fetchGeneration += 1;
    this.#pages.clear();
    this.#pendingPages.clear();
    this.#prependedCount = 0;
    this.#visiblePage = 0;
    this.#isLoading = false;
    this.#autoLoadObserver?.disconnect();

    const observer = new MutationObserver(() => {
      const { grid } = this.refs;
      if (!grid) return;
      observer.disconnect();
      this.#initialDomCount = grid.querySelectorAll(':scope > [ref="cards[]"]').length;
      this.#updatePageNav();
      this.#observeAutoLoad();
      this.#prefetchNextPage();
    });

    const { grid } = this.refs;
    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true,
      });
      setTimeout(() => observer.disconnect(), 3000);
    }
  };

  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('collection-grid-layout-component: missing section-id attribute');
    return id;
  }
}

if (!customElements.get('collection-grid-layout-component')) {
  customElements.define('collection-grid-layout-component', CollectionGridLayoutComponent);
}
